import React, { useEffect, useState } from "react";
import { APP_MESSAGES, ENTITY_TYPE } from "src/shared/config/src";
import { deleteCommentOnPost, deleteObservationCommentsList, fetchObservationCommentsList, fetchPostReactionsList } from "src/shared/services/src";
import { ChildrenStoreData, ReactionCommentData } from "src/shared/types/src";
import { getCurrentChild } from "src/shared/redux-state/src";
import { useAppDispatch, useAppSelector } from "../services/store";
import { getNameInitials } from "src/shared/utils/src";
import { AxiosError } from "axios";
import { GlobalErrorActions } from "src/shared/redux-state/src/lib/globalError/GlobalErrorSlice";
import ProfileImageView from "./commonComponents/ProfileImageView";
import { EyengageActions } from "src/shared/redux-state/src/lib/eyengage/EyengageSlice";
import { CustomButton } from "./commonComponents";
import { FaRegTrashCan } from "react-icons/fa6";
import { CommentListLoader } from "./loader";


type IProps = {
    entityId: string,
    entityType: string,
    newComment?: ReactionCommentData,
    onDeleteComment?: Function,
    updatedComments?: ReactionCommentData[]
}

const CommentList: React.FC<IProps> = ({ entityId, entityType, newComment, onDeleteComment, updatedComments }) => {
    const dispatch = useAppDispatch();
    const [commentsLoading, setCommentsLoading] = useState<boolean>(false);
    const [commentList, setCommentList] = useState<ReactionCommentData[]>([]);
    const [removingComment, setRemovingComment] = useState<string[]>([]);

    const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);

    useEffect(() => {
        updatedComments && setCommentList(updatedComments);
    }, [updatedComments]);

    useEffect(() => {
        const fetchComments = async (entityId: string, entityType: string) => {
            try {
                setCommentsLoading(true);
                let response;
                if (entityType === ENTITY_TYPE.OBSERVATION) {
                    response = await fetchObservationCommentsList({ child: currentChild, observationId: entityId });
                    console.log("Response in for fetch comments is : ", response);
                    if (response && Object.keys(response).length) {
                        setCommentList(response?.comments?.data ?? []);
                    } else setCommentList([]);
                } else if (entityType === ENTITY_TYPE.EYENGAGE) {
                    response = await fetchPostReactionsList({ child: currentChild, postId: entityId });
                    console.log("Response in compomnent is : ", response);
                    if (response && Object.keys(response).length) {
                        setCommentList(response?.reactions?.comments?.data ?? []);
                    } else setCommentList([]);
                }
            } catch (error) {
                setCommentList([]);
                await dispatch(GlobalErrorActions.setError({ isError: true, message: 'Could not fetch comments.' }));
            } finally {
                setCommentsLoading(false);
            }
        }
        fetchComments(entityId, entityType);
    }, [currentChild, dispatch, entityId, entityType]);

    useEffect(() => {
        if (newComment) {
            setCommentList(prevList => [newComment, ...prevList]);
        }
    }, [newComment]);



    const handleRemoveComment = async (commentId: string) => {
        console.log("comment id in delete comment api : ", commentId);
        try {
            setRemovingComment([...removingComment, commentId]);
            let response;
            if (entityType === ENTITY_TYPE.OBSERVATION) {
                response = await deleteObservationCommentsList({
                    child: currentChild,
                    observationId: entityId,
                    commentId
                });
                setCommentList(commentList.filter((c) => c.id !== commentId));
                if (typeof onDeleteComment == 'function') onDeleteComment();
            }
            if (entityType === ENTITY_TYPE.EYENGAGE) {
                response = await deleteCommentOnPost({ child: currentChild, postId: entityId, commentId });
                if(response?.reactions?.comments?.data) {
                    setCommentList(commentList.filter((c) => c.id !== commentId));
                    if (onDeleteComment) onDeleteComment();
                    setCommentList(response?.reactions?.comments?.data);
                    // update the comment count in redux 
                    await dispatch(EyengageActions.updateCommentCountInPostById({
                        postId: entityId,
                        updatedCommentCount: response?.reactions?.comments.count
                    }));
                }
                
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                const errors = error?.response?.data?.errors || APP_MESSAGES.generalErrorMessage;
                await dispatch(GlobalErrorActions.setError({
                    isError: true,
                    message: (errors)
                }));
            }
        } finally {
            setRemovingComment(removingComment.filter((r) => r !== commentId));
        }
    }

    return (
        <div style={{ height: commentsLoading ? '330px' : 'auto' }}>
            {
                commentsLoading ?
                    <>
                        <CommentListLoader />
                    </> :
                    <div>
                        {commentList.length ? <>{
                            commentList.map((item, index) => <CommentListItem commentItem={item} index={index}
                                deleteComment={handleRemoveComment} />)
                        }</> : <NoComments />}
                    </div>
            }
        </div>
    )
}

const NoComments = () => {
    return (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '330px' }}>
            <div className="w-100 text-center">
                <div className="mg-b-5 fs-14 text-black fw-bold">No comments yet</div>
                <div className="mg-b-0 fs-14 text-black">Start the conversation</div>
            </div>
        </div>
    )
}

const CommentListItem = ({ commentItem, deleteComment, index }: {
    commentItem: ReactionCommentData, deleteComment: (commentId: string) => void, index: number,
}) => {
    const [, setNameInitials] = useState('');


    useEffect(() => {
        if (commentItem.user && commentItem.user.name && !commentItem.user.photo) {
            const nameInitials = getNameInitials(commentItem.user.name || '');
            setNameInitials(nameInitials);
        }
    }, [commentItem]);

    return (
        <div className={`mx-n-15 pd-x-15 d-flex ${index === 0 ? 'pd-b-15' : 'pd-y-15 border-top border-gray-700'}`}>
            {
                commentItem.user ?
                    <ProfileImageView name={commentItem.user.name} imageUrl={commentItem.user.photo} /> : null
            }
            <div className="flex-1 mg-l-10">
                <div className="d-flex align-items-center">
                    <div className="fw-bold fs-14 text-black flex-1">{commentItem.user.name ?? '-'}</div>
                    <div className="fs-12 text-gray-300 text-nowrap mg-l-10 cursor-pointer">{commentItem.date} {commentItem.self && <CustomButton color="link" icon={<FaRegTrashCan size={14} color="#D82F2F" />} onClick={() => deleteComment(commentItem.id)} customBtnStyle={`w-auto p-0 mg-l-5 d-inline-block pd-x-5`} style={{ height: '18px' }} />}</div>
                </div>
                <div className="fs-12 text-black">{commentItem.text}</div>
            </div>
            

            
        </div>
    )
}

export default CommentList;